// The TODAY cards (Today Screen v2, the "Signal" re-layout): the approval-queue banner that routes to
// the Approvals inbox, the "In motion" open-Task list, and the "What Ritona did" card (the schedule-health
// banner with the activity log behind an on-demand toggle). Every count, row, and status derives from the
// composed TodayProjection (G-182): the banner counts the SAME pending Gates the Approvals inbox carries,
// the motion rows are the non-terminal Task set (nothing forgotten), a blocked row's pill IS its recorded
// parked reason, and the log is the sanitised Audit projection (G-058) rendered by the existing rail.
// Owner initials only — never a raw owner id (G-197). Tokens only (G-181).

import { useState } from 'react';
import type {
  ActivityRow,
  NonTerminalTaskRow,
  ScheduleHealthProjection,
  TodayProjection,
} from '../projections.ts';
import type { ApprovalTriageSummary } from '../approval-triage.ts';
import { Avatar } from '../../app/Avatar.tsx';
import { Icon } from '../../app/Icon.tsx';
import { ownerInitials } from '../owner-display.ts';
import { EmptyState } from '../primitives.tsx';
import { ActivityRail } from './ActivityRail.tsx';

/** The amber approval banner — full pending-Gate counts; opens the complete Approvals queue. */
export function WaitingBanner({
  summary,
  onOpenApprovals,
}: {
  readonly summary: ApprovalTriageSummary;
  readonly onOpenApprovals: () => void;
}): React.JSX.Element | null {
  if (summary.total === 0) return null;
  const split = `${summary.readyForYou} ready for you · ${summary.setupNeeded} setup needed · ${summary.withSomeoneElse} with someone else`;
  return (
    <button
      type="button"
      className="tw-banner"
      onClick={onOpenApprovals}
      aria-label={`Open Approvals: ${summary.total} waiting`}
    >
      <span className="tw-count">{summary.total}</span>
      <span className="tw-body">
        <span className="tw-title">Approval queue</span>
        <span className="tw-split">{split}</span>
      </span>
      <span className="tw-go">
        Approvals
        <Icon name="chevron" size={14} />
      </span>
    </button>
  );
}

/** One in-motion row's status pill — derived ONLY from the row's lifecycle fields (G-182). */
function motionPill(task: NonTerminalTaskRow): { readonly label: string; readonly warn: boolean } {
  switch (task.state) {
    case 'working':
      return { label: 'Running', warn: false };
    case 'blocked':
      return { label: task.parkedReason ?? 'Parked', warn: true };
    case 'briefed':
      return { label: 'Queued', warn: false };
    case 'draft':
      return { label: 'Draft', warn: false };
    default:
      return { label: task.state, warn: false };
  }
}

/** The "In motion" list — every non-terminal Task; a row opens the Task-detail modal. */
export function MotionList({
  tasks,
  workload,
  onOpenTask,
  onOpenBoard,
}: {
  readonly tasks: readonly NonTerminalTaskRow[];
  readonly workload: TodayProjection['workload'];
  readonly onOpenTask: (id: string) => void;
  readonly onOpenBoard: () => void;
}): React.JSX.Element {
  const parked = tasks.filter((task) => task.state === 'blocked').length;
  return (
    <div className="tm-motion">
      <div className="tm-head">
        <h2 className="h-section">In motion</h2>
        <span className="mono-label">
          {workload.openCount} open · {parked} parked
        </span>
        <span className="spacer" aria-hidden="true" />
        <button type="button" className="tm-board" onClick={onOpenBoard} aria-label="Tasks board">
          Tasks board
        </button>
      </div>
      {tasks.length === 0 ? (
        <EmptyState>Nothing is in motion right now.</EmptyState>
      ) : (
        tasks.map((task) => {
          const pill = motionPill(task);
          return (
            <button
              key={task.id}
              type="button"
              className="tm-row"
              data-open-task={task.id}
              onClick={() => onOpenTask(task.id)}
              aria-label={`Open task: ${task.title}`}
            >
              <Avatar initials={ownerInitials(task.ownerUserId)} size="sm" />
              <span className="tm-title">{task.title}</span>
              <span className={pill.warn ? 'tm-pill warn' : 'tm-pill'}>{pill.label}</span>
            </button>
          );
        })
      )}
    </div>
  );
}

/** Count the schedule/background rows that are failing or interrupted (the "needs a look" figure). */
function attentionCount(health: ScheduleHealthProjection): number {
  const rows = [...health.schedules, ...health.backgroundJobs];
  return rows.filter((row) => row.health === 'failing' || row.health === 'interrupted').length;
}

/** The "What Ritona did" card — the schedule-health banner + the activity log ON DEMAND. */
export function RitonaLog({
  health,
  activity,
}: {
  readonly health: ScheduleHealthProjection;
  readonly activity: readonly ActivityRow[];
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const attention = attentionCount(health);
  const jobs = health.schedules.length + health.backgroundJobs.length;
  return (
    <div className="ts-did">
      <div className="tm-head">
        <h2 className="h-section">What Ritona did</h2>
      </div>
      <div className={attention === 0 ? 'ts-banner' : 'ts-banner warn'}>
        <span className="ts-ic" aria-hidden="true">
          <Icon name={attention === 0 ? 'check' : 'pulse'} size={16} />
        </span>
        <span className="ts-txt">
          {attention === 0
            ? `All clear · ${jobs} ${jobs === 1 ? 'job' : 'jobs'}`
            : `${attention} of ${jobs} jobs need a look`}
        </span>
        <button
          type="button"
          className="ts-log"
          aria-expanded={open}
          aria-label={`${open ? 'Hide' : 'Show'} the activity log`}
          onClick={() => setOpen((wasOpen) => !wasOpen)}
        >
          {open ? 'Hide' : 'Log'}
        </button>
      </div>
      {open ? <ActivityRail rows={activity} /> : null}
    </div>
  );
}
